import styled from "styled-components";
import breakpoints from "../Utils/Breakpoints";

/* ---------------------------------------------------------------------------
   Joel Michael — Waitlist hero (section 1 of the new Figma design)
   Dark luxury theme. Responsive: two columns on desktop, stacked on mobile.
--------------------------------------------------------------------------- */

export const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  background: var(--jm-bg);
  color: var(--jm-text);
  display: flex;
  flex-direction: column;
`;

export const Brand = styled.h1`
  margin: 0;
  padding: 48px 24px 0;
  text-align: center;
  font-family: var(--jm-font-sans);
  font-weight: 500;
  font-size: 28px;
  line-height: 1;
  letter-spacing: 0;
  color: var(--jm-text);

  @media (max-width: ${breakpoints.small}) {
    padding-top: 32px;
    font-size: 22px;
  }
`;

export const Hero = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 72px 64px 96px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 72px;

  @media (max-width: ${breakpoints.medium}) {
    padding: 56px 40px 72px;
    gap: 48px;
  }
  @media (max-width: ${breakpoints.small}) {
    grid-template-columns: 1fr;
    padding: 40px 22px 0;
    gap: 40px;
  }
`;

export const Copy = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Line-break helpers: force a break only on the given viewport, otherwise the
   surrounding whitespace collapses to a single space. */
export const MobileBreak = styled.br`
  display: none;
  @media (max-width: ${breakpoints.small}) {
    display: initial;
  }
`;

export const DesktopBreak = styled.br`
  display: initial;
  @media (max-width: ${breakpoints.small}) {
    display: none;
  }
`;

export const Eyebrow = styled.p`
  margin: 0 0 22px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16.5px;
  letter-spacing: 4.62px;
  text-transform: uppercase;
  color: var(--jm-kicker);

  &::before {
    content: "";
    width: 28px;
    height: 1px;
    background: var(--jm-kicker);
  }
`;

export const Headline = styled.h2`
  margin: 0;
  font-family: var(--jm-font-sans);
  font-weight: 300;
  font-size: 70px;
  line-height: 1.02;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--jm-text);

  @media (max-width: ${breakpoints.large}) {
    font-size: 58px;
  }
  @media (max-width: ${breakpoints.small}) {
    font-size: 48px;
    line-height: 53px;
    letter-spacing: 1.66px;
  }
`;

export const Tagline = styled.p`
  margin: 26px 0 0;
  max-width: 472px;
  font-size: 15px;
  font-weight: 400;
  line-height: 24.38px;
  letter-spacing: 0;
  color: var(--jm-muted);

  /* Mobile: keep the two authored lines exactly — line 1 stays whole
     (down to it "…the art of"), line 2 is "being felt, not seen.".
     The break only happens at <MobileBreak />, and the font scales down a
     touch on narrow screens (e.g. iPhone 6 / 375px) so line 1 never wraps. */
  @media (max-width: ${breakpoints.small}) {
    white-space: nowrap;
    font-size: clamp(11px, 3.6vw, 15px);
  }
`;

/* Progress ---------------------------------------------------------------- */

export const ProgressWrap = styled.div`
  margin: 40px 0 30px;
  max-width: 460px;

  ${(props) =>
    props.$wide &&
    `
    max-width: none;
    margin-top: 48px;
  `}

  @media (min-width: calc(${breakpoints.small} + 1px)) {
    ${(props) => props.$wide && `margin-left: 60px; margin-right: 60px;`}
  }
  @media (min-width: ${breakpoints.medium}) {
    ${(props) => props.$wide && `margin-left: 140px; margin-right: 140px;`}
  }
`;

export const ProgressMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 19.5px;
  letter-spacing: 0.32px;
  color: var(--jm-muted);

  strong {
    color: var(--jm-text);
    font-weight: 600;
    margin-right: 6px;
  }
`;

export const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 100px;
  background: rgba(230, 208, 187, 0.16);
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.$pct}%;
  border-radius: 100px;
  background: #959e8f;
  transition: width 0.6s ease;
`;

/* Form -------------------------------------------------------------------- */

export const Form = styled.form`
  max-width: 460px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: ${breakpoints.small}) {
    gap: 12px;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 15px 16px;
  background: transparent;
  border: 1px solid
    ${(props) => (props.$error ? "#B4553F" : "var(--jm-line)")};
  border-radius: 2px;
  color: var(--jm-text);
  font-family: var(--jm-font-sans);
  font-size: 14px;
  letter-spacing: 0.01em;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--jm-faint);
  }
  &:focus {
    outline: none;
    border-color: var(--jm-line-strong);
  }
`;

export const Submit = styled.button`
  margin-top: 6px;
  width: 100%;
  height: 56px;
  padding: 0 32px;
  border: none;
  border-radius: 2px;
  background: #55654B;
  color: #EAE7DD;
  font-family: var(--jm-font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;

  &:hover {
    background: #3d4837;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const Membership = styled.p`
  margin: 20px 0 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 19.5px;
  letter-spacing: 0.32px;
  color: rgba(184, 185, 173, 0.7);

  strong {
    color: #eae7dd;
    font-weight: 400;
  }

  span {
    margin: 0 8px;
    color: var(--jm-faint);
  }
`;

export const ErrorText = styled.span`
  margin-top: -4px;
  font-size: 12px;
  color: #c8735c;
`;

/* Media (product image) --------------------------------------------------- */

export const Media = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  max-height: 560px;
  overflow: hidden;
  background: #0c0d09;

  @media (max-width: ${breakpoints.small}) {
    max-height: none;
    aspect-ratio: 3 / 4;
    order: 3;
  }
`;

export const MediaImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/* Stats band -------------------------------------------------------------- */

export const StatsBand = styled.section`
  width: 100%;
  background: #55654B;
  display: grid;
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: ${breakpoints.small}) {
    grid-template-columns: repeat(2, 1fr);
    margin-top: 40px;
  }
`;

export const StatItem = styled.div`
  padding: 40px 24px;
  text-align: center;
  border-left: 1px solid rgba(20, 21, 15, 0.16);

  &:first-child {
    border-left: none;
  }

  @media (max-width: ${breakpoints.small}) {
    padding: 30px 16px;

    &:nth-child(odd) {
      border-left: none;
    }
    &:nth-child(n + 3) {
      border-top: 1px solid rgba(20, 21, 15, 0.16);
    }
  }
`;

export const StatValue = styled.p`
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: 0.08em;
  color: #EAE7DD;

  @media (max-width: ${breakpoints.small}) {
    font-size: 19px;
  }
`;

export const StatLabel = styled.p`
  margin: 0;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #E1FEC4B2;
`;

/* ---------------------------------------------------------------------------
   Shared section primitives (sections 4-10 of the Figma "Waitlist" flow)
   Figma: 1512px wide, 112px vertical padding, 48px horizontal,
   1px #EAE7DD @10% top rule.
--------------------------------------------------------------------------- */

export const Section = styled.section`
  width: 100%;
  padding: 112px 48px;
  background: ${(props) => props.$bg || "var(--jm-bg)"};
  border-top: 1px solid rgba(234, 231, 221, 0.1);
  ${(props) =>
    props.$outlined &&
    `
    border-top: 1px solid rgba(234, 231, 221, 0.16);
    border-bottom: 1px solid rgba(234, 231, 221, 0.16);
    border-left: none;
    border-right: none;
  `}

  @media (max-width: ${breakpoints.medium}) {
    padding: 80px 40px;
  }
  @media (max-width: ${breakpoints.small}) {
    padding: 64px 22px;
  }
`;

export const SectionInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  ${(props) =>
    props.$bg &&
    `
    background: ${props.$bg};
    padding: 56px 48px;
    border-radius: 4px;
  `}

  @media (max-width: ${breakpoints.small}) {
    ${(props) => props.$bg && `padding: 32px 22px;`}
  }
`;

export const SectionEyebrow = styled.p`
  margin: 0 0 22px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16.5px;
  letter-spacing: 4.62px;
  text-transform: uppercase;
  color: var(--jm-kicker);

  ${(props) => props.$center && "justify-content: center;"}

  &::before {
    content: "";
    width: 28px;
    height: 1px;
    background: var(--jm-kicker);
  }
`;

export const SectionHeadline = styled.h2`
  margin: 0;
  font-family: var(--jm-font-sans);
  font-weight: 300;
  font-size: 44px;
  line-height: 1.14;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--jm-text);
  ${(props) => props.$center && "text-align: center;"}

  @media (max-width: ${breakpoints.large}) {
    font-size: 36px;
  }
  ${(props) =>
    props.$snug &&
    `
    font-size: 36px;
    @media (max-width: ${breakpoints.large}) {
      font-size: 32px;
    }
  `}
  /* Mobile section headings switch to Jost per the Figma design. */
  @media (max-width: ${breakpoints.small}) {
    font-family: var(--jm-font-display);
    font-size: 30px;
    line-height: 1.1;
  }
`;

export const SectionBody = styled.p`
  margin: 24px 0 0;
  max-width: ${(props) => (props.$center ? "760px" : "520px")};
  font-size: 15px;
  line-height: 24.38px;
  color: var(--jm-muted);
  ${(props) => props.$center && "text-align: center; margin-left: auto; margin-right: auto;"}
`;

/* Philosophy -------------------------------------------------------------- */

export const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: ${(props) => (props.$stretch ? "stretch" : "center")};
  gap: 72px;

  @media (max-width: ${breakpoints.medium}) {
    gap: 48px;
    ${(props) => props.$stackEarly && `grid-template-columns: 1fr; gap: 40px;`}
  }
  @media (max-width: ${breakpoints.small}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const SageCard = styled.div`
  margin-top: 16px;
  padding: 22px 24px;
  background: #55654b;
  border-radius: 2px;
`;

export const SageCardTitle = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #E1FEC4;

  @media (max-width: ${breakpoints.small}) {
    text-align: left;
  }
`;

export const SageCardBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 22.75px;
  color: #EAE7DDCC;

  @media (max-width: ${breakpoints.small}) {
    text-align: left;
  }
`;

export const SectionImage = styled.img`
  width: 100%;
  height: ${(props) => (props.$fill ? "auto" : "100%")};
  max-height: ${(props) => (props.$fill ? "none" : "620px")};
  aspect-ratio: ${(props) => (props.$fill ? "597.5 / 747.375" : "auto")};
  object-fit: cover;
  display: block;
  border-radius: 2px;

  @media (max-width: ${breakpoints.small}) {
    max-height: ${(props) => (props.$fill ? "none" : "420px")};
  }
`;

/* Outlined frame around Philosophy / Founder imagery. */
export const ImageFrame = styled.div`
  overflow: hidden;
  padding: 0.5px;
  background: #55654b;
  border-radius: 1px;
  ${(props) =>
    props.$fill &&
    `
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `}

  & > img {
    width: 100%;
    height: ${(props) => (props.$fill ? "auto" : "auto")};
    display: block;
  }
`;

/* Protocol (status quo vs our standard) ----------------------------------- */

export const ProtocolGrid = styled.div`
  margin-top: 48px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  /* Mobile: a horizontal slide of paired columns — the DOM alternates
     problem/solution, so column flow puts every red card on the top row
     and its sage counterpart directly beneath it. */
  @media (max-width: ${breakpoints.small}) {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-template-rows: auto auto;
    grid-auto-columns: 90%;
    align-items: start;
    gap: 14px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* Bleed to the section edges so cards can scroll under the padding. */
    // margin-left: -22px;
    // margin-right: -22px;
    padding: 0 22px;

    &::-webkit-scrollbar {
      display: none;
    }

    > * {
      scroll-snap-align: start;
    }
  }
`;

export const ProtocolCard = styled.div`
  padding: 26px 28px;
  border-radius: 2px;
  background: ${(props) => (props.$solution ? "#55654B" : "#2A1414B2")};
  border: 1px solid
    ${(props) => (props.$solution ? "#8A91744D" : "#7A3B3B66")};

  @media (max-width: ${breakpoints.small}) {
    padding: 26px 22px;
  }
`;

export const ProtocolLabel = styled.p`
  margin: 0 0 12px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.30em;
  text-transform: uppercase;
  color: ${(props) => (props.$solution ? "#E1FEC4B2" : "#C98B8B")};
`;

export const ProtocolText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: ${(props) => (props.$solution ? "#EAE7DD" : "#EAE7DD")};

  @media (max-width: ${breakpoints.small}) {
    font-size: 15px;
    line-height: 24px;
  }
`;

/* Yours soon enough ------------------------------------------------------- */

export const TripleGrid = styled.div`
  margin-top: 48px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  /* Mobile only: a horizontal carousel instead of a stacked column. */
  @media (max-width: ${breakpoints.small}) {
    margin-top: 32px;
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    > * {
      flex: 0 0 85%;
      width: 85% !important;
      scroll-snap-align: start;
    }
  }
`;

export const TripleImg = styled.img`
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  display: block;
  border-radius: 2px;

  @media (max-width: ${breakpoints.small}) {
    aspect-ratio: 3 / 4.6;
  }
`;

/* Comparison / pricing ---------------------------------------------------- */

export const PlanGrid = styled.div`
  margin-top: 48px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;

  @media (min-width: calc(${breakpoints.small} + 1px)) {
    margin-left: 60px;
    margin-right: 60px;
  }

  @media (min-width: ${breakpoints.medium}) {
    margin-left: 140px;
    margin-right: 140px;
  }

  @media (max-width: ${breakpoints.small}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const PlanCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 32px 29px;
  border-radius: 2px;
  background: ${(props) => (props.$paid ? "#55654B" : "#161815")};
  border: 1px solid
    ${(props) => (props.$paid ? "transparent" : "rgba(234, 231, 221, 0.16)")};

  @media (max-width: ${breakpoints.small}) {
    padding: 24px 29px;
  }
`;

export const PlanBadge = styled.span`
  position: absolute;
  top: -11px;
  right: 24px;
  padding: 5px 12px;
  border-radius: 2px;
  background: #46523E;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #EAE7DD;
`;

export const PlanLabel = styled.p`
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${(props) => (props.$paid ? "#E1FEC4B2" : "var(--jm-muted)")};
`;

export const PlanPrice = styled.p`
  margin: 0 0 24px;
  font-size: 44px;
  font-weight: 300;
  letter-spacing: 0.01em;
  color: #EAE7DD;

  @media (max-width: ${breakpoints.small}) {
    font-size: 36px;
  }
`;

export const PerkList = styled.ul`
  margin: 0 0 28px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Perk = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  line-height: 20px;
  color: ${(props) => (props.$paid ? "#EAE7DD" : "var(--jm-muted)")};

  &::before {
    content: "✓";
    flex-shrink: 0;
    color: ${(props) => (props.$paid ? "#E1FEC4B2" : "var(--jm-kicker)")};
  }
`;

export const GhostButton = styled.button`
  width: 100%;
  height: 52px;
  margin-top: auto;
  border: none;
  border-radius: 2px;
  background: #46523E;
  color: #EAE7DD;
  font-family: var(--jm-font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: default;
`;

export const ComparisonFootnote = styled.p`
  margin: 40px 0 0;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--jm-muted);

  strong {
    display: block;
    margin-top: 6px;
    color: #EAE7DD;
    font-weight: 600;
  }
`;

/* Personal signatures ----------------------------------------------------- */

export const SignatureRow = styled.div`
  margin: 48px 0 0;
  display: flex;
  gap: 24px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: 0 0 32%;
    scroll-snap-align: start;
  }

  @media (max-width: ${breakpoints.medium}) {
    margin: 40px 0 0;
    gap: 16px;

    > * {
      flex-basis: 60%;
    }
  }
  @media (max-width: ${breakpoints.small}) {
    margin: 32px 0 0;

    > * {
      flex-basis: 84%;
    }
  }
`;

export const SignatureCard = styled.figure`
  margin: 0;
  padding: 28px;
  border-radius: 2px;
  background: #55654b;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
`;

export const SignatureQuote = styled.blockquote`
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #E1FEC4B2;
`;

export const SignatureAuthor = styled.figcaption`
  text-align: right;
  font-size: 13px;
  color: #EAE7DD;
`;

/* Founder ----------------------------------------------------------------- */

export const FounderCopy = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: calc(${breakpoints.small} + 1px)) {
    margin-top: -66px;
  }
`;

export const FounderQuote = styled.p`
  margin: 24px 0 0;
  font-size: 15px;
  line-height: 24px;
  color: #EAE7DD;
  text-align: justify;
`;

export const FounderParagraph = styled.p`
  margin: 18px 0 0;
  font-size: 14px;
  line-height: 23px;
  color: var(--jm-muted);
  text-align: justify;
`;

export const FounderSignature = styled.p`
  margin: 28px 0 24px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--jm-kicker);
  text-align: left;
`;

export const InlineCta = styled.button`
  height: 52px;
  padding: 0 32px;
  margin-top: auto;
  align-self: flex-start;
  border: none;
  border-radius: 2px;
  background: #55654b;
  color: #EAE7DD;
  font-family: var(--jm-font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #3d4837;
  }

  @media (max-width: ${breakpoints.small}) {
    align-self: stretch;
    width: 100%;
  }
`;

/* Frequent inquiries ------------------------------------------------------ */

export const FaqItem = styled.div`
  border-bottom: 1px solid rgba(234, 231, 221, 0.12);
`;

export const FaqButton = styled.button`
  width: 100%;
  padding: 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: var(--jm-font-sans);
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${(props) => (props.$open ? "#8A9174" : "#EAE7DD")};
  transition: color 0.2s ease;

  span {
    flex-shrink: 0;
    font-size: 18px;
    font-weight: 300;
    color: ${(props) => (props.$open ? "#8A9174" : "var(--jm-muted)")};
  }

  @media (max-width: ${breakpoints.small}) {
    font-size: 14px;
    letter-spacing: 0.1em;
    line-height: 20px;
  }
`;

export const FaqAnswer = styled.p`
  margin: 0;
  padding: 0 0 24px;
  max-width: none;
  font-family: var(--jm-font-sans);
  font-size: 15px;
  line-height: 24px;
  color: #B8B9ADB2;

  @media (max-width: ${breakpoints.small}) {
    font-size: 14px;
    line-height: 22px;
  }
`;

/* Footer ------------------------------------------------------------------ */

export const FooterBar = styled.footer`
  width: 100%;
  padding: 56px 48px;
  border-top: 1px solid rgba(234, 231, 221, 0.1);
  text-align: center;

  @media (max-width: ${breakpoints.small}) {
    padding: 40px 22px;
  }
`;

export const FooterBrand = styled.p`
  position: relative;
  margin: 0 0 28px;
  padding-bottom: 14px;
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.34em;
  color: var(--jm-text);

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 30%;              /* ← tune this */
    height: 1px;
    background: #E1FEC499;
  }
`;

export const FooterSocials = styled.div`
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 10px 24px;
  border: 1px solid rgba(234, 231, 221, 0.16);
  border-radius: 100px;

  a {
    display: inline-flex;
    opacity: 0.72;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }

  img {
    width: 18px;
    height: 18px;
    display: block;
  }

  @media (max-width: ${breakpoints.small}) {
    gap: 12px;
    padding: 9px 18px;
  }
`;

export const FooterHandle = styled.span`
  margin-left: 4px;
  font-size: 13px;
  letter-spacing: 0.2em;
  color: var(--jm-muted);

  @media (max-width: ${breakpoints.small}) {
    font-size: 11px;
    letter-spacing: 0.14em;
  }
`;

export const FooterRule = styled.hr`
  margin: 32px auto;
  max-width: 1000px;
  border: none;
  border-top: 1px solid rgba(234, 231, 221, 0.1);
`;

export const FooterCopy = styled.p`
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.16em;
  color: var(--jm-faint);
`;

/* Skeleton loader (waitlist count) ----------------------------------------- */

export const Skeleton = styled.span`
  display: inline-block;
  width: ${(props) => props.$width || "48px"};
  height: ${(props) => props.$height || "1em"};
  border-radius: 4px;
  vertical-align: middle;
  background: linear-gradient(
    90deg,
    rgba(234, 231, 221, 0.08) 25%,
    rgba(234, 231, 221, 0.18) 37%,
    rgba(234, 231, 221, 0.08) 63%
  );
  background-size: 400% 100%;
  animation: jm-skeleton 1.4s ease infinite;

  @keyframes jm-skeleton {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
`;
